import { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigationType, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LocationContext } from '../contexts/LocationContext';
import {
    Coordinates, formatDistance, formatInstallationDate, getCoordinates, getDistance,
    getPointLabel, LocationCityGroup, LocationObjectGroup, LocationSort,
} from '../helpers/locationBrowse';

interface IFilteredLocation { groups: LocationCityGroup[]; sort: LocationSort; position: Coordinates | null; filterKey: string; }

interface LocationMetricProps {
    id: string;
    sort: LocationSort;
    distance: number | null;
    installationDate?: string;
}

const LocationMetric = memo(({ id, sort, distance, installationDate }: LocationMetricProps) => {
    const { t, i18n } = useTranslation();
    if (sort === 'alphabetical') return null;
    const locale = i18n.language.startsWith('pl') ? 'pl-PL' : 'en-GB';
    const value = (sort === 'distance' ? formatDistance(distance, locale) : formatInstallationDate(installationDate, locale))
        ?? t('Brak danych');
    return (
        <span id={id} className="location-row-metric">
            {value}
        </span>
    );
});

type ToggleGroup = (key: 'expandedCity' | 'expandedLocation', id: string, open: boolean) => void;
interface GroupRowProps {
    contentId: string;
    open: boolean;
    sort: LocationSort;
    position: Coordinates | null;
    onToggle: ToggleGroup;
}

const LocationObject = memo(({ object, contentId, open, sort, position, onToggle }:
    GroupRowProps & { object: LocationObjectGroup }) => {
    const { t } = useTranslation();
    const showMetric = sort !== 'alphabetical';
    return (
        <li className="location-object">
            <button type="button" className={`location-row${showMetric ? ' location-row--metric' : ''}`}
                aria-label={`${object.label} (${object.points.length})`}
                aria-describedby={showMetric ? `${contentId}-metric` : undefined}
                onClick={() => onToggle('expandedLocation', object.id, open)} aria-expanded={open} aria-controls={open ? contentId : undefined}>
                <span className="location-row-label">{object.label}</span>
                <LocationMetric id={`${contentId}-metric`} sort={sort} distance={object.distance} installationDate={object.installationDate} />
                <span className="location-row-count" aria-hidden="true">{object.points.length}</span>
                <span className="location-row-icon" aria-hidden="true">{open ? '−' : '+'}</span>
            </button>
            {open && (
                <ol id={contentId} className="location-points" aria-label={t('Lista punktów')}>
                    {object.points.map((point, pointIndex) => {
                        const coords = sort === 'distance' ? getCoordinates(point) : null;
                        const distance = position && coords ? getDistance(position, coords) : null;
                        const metricId = `${contentId}-point-${pointIndex}-metric`;
                        return (
                            <li key={point.id}>
                                <Link className="location-point-link" to={`/lokalizacje/${point.id}`}
                                    aria-label={getPointLabel(point)} aria-describedby={showMetric ? metricId : undefined}>
                                    <span>{getPointLabel(point)}</span>
                                    <LocationMetric id={metricId} sort={sort} distance={distance} installationDate={point.date_of_installation} />
                                </Link>
                            </li>
                        );
                    })}
                </ol>
            )}
        </li>
    );
});

const LocationCity = memo(({ city, contentId, open, expandedObjectId, sort, position, onToggle }:
    GroupRowProps & { city: LocationCityGroup; expandedObjectId?: string }) => {
    const { t } = useTranslation();
    const showMetric = sort !== 'alphabetical';
    const cityLabel = city.city || t('Bez miejscowości');
    return (
        <li className="location-city">
            <button type="button" className={`location-row${showMetric ? ' location-row--metric' : ''}`}
                aria-label={`${cityLabel} (${city.total})`}
                aria-describedby={showMetric ? `${contentId}-metric` : undefined}
                onClick={() => onToggle('expandedCity', city.id, open)} aria-expanded={open} aria-controls={open ? contentId : undefined}>
                <span className="location-row-label">{cityLabel}</span>
                <LocationMetric id={`${contentId}-metric`} sort={sort} distance={city.distance} installationDate={city.installationDate} />
                <span className="location-row-count" aria-hidden="true">{city.total}</span>
                <span className="location-row-icon" aria-hidden="true">{open ? '−' : '+'}</span>
            </button>
            {open && (
                <ul id={contentId} className="location-object-groups" role="list" aria-label={t('Lista obiektów')}>
                    {city.objects.map((object, objectIndex) => (
                        <LocationObject key={object.id} object={object} contentId={`${contentId}-object-${objectIndex}`}
                            open={expandedObjectId === object.id} sort={sort} position={position} onToggle={onToggle} />
                    ))}
                </ul>
            )}
        </li>
    );
});

export const FilteredLocation: React.FC<IFilteredLocation> = ({ groups, sort, position, filterKey }) => {
    const { t } = useTranslation();
    const { expansionHistory } = useContext(LocationContext);
    const location = useLocation();
    const navigationType = useNavigationType();
    const [searchParams] = useSearchParams();
    const urlExpansion = { expandedCity: searchParams.get('expandedCity'), expandedLocation: searchParams.get('expandedLocation') };
    const urlKey = JSON.stringify(urlExpansion);
    const [state, setState] = useState(() => ({ entryKey: location.key, filterKey, urlKey,
        expansion: expansionHistory.current.get(location.key) ?? urlExpansion }));
    let current = state;
    if (state.entryKey !== location.key || state.filterKey !== filterKey || state.urlKey !== urlKey) {
        current = { entryKey: location.key, filterKey, urlKey,
            expansion: expansionHistory.current.get(location.key)
                ?? (navigationType !== 'POP' && state.filterKey === filterKey && state.urlKey === urlKey
                    ? state.expansion : urlExpansion) };
        setState(current);
    }
    const stateRef = useRef(current);
    stateRef.current = current;
    useEffect(() => {
        expansionHistory.current.set(state.entryKey, state.expansion);
    }, [expansionHistory, state]);
    const cityParam = current.expansion.expandedCity;
    const objectParam = current.expansion.expandedLocation;
    const expandedCity = cityParam !== null
        ? groups.find(city => city.id === cityParam || (city.city && city.city === cityParam))
        : groups.length === 1 ? groups[0] : undefined;
    const expandedObject = objectParam !== null
        ? expandedCity?.objects.find(object => object.id === objectParam || object.label === objectParam.trim())
        : expandedCity?.objects.length === 1 ? expandedCity.objects[0] : undefined;

    const toggleGroup = useCallback<ToggleGroup>((key, id, open) => {
        const previous = stateRef.current;
        const expansion = { ...previous.expansion, [key]: open ? '' : id };
        if (key === 'expandedCity') expansion.expandedLocation = null;
        const next = { ...previous, expansion };
        expansionHistory.current.set(next.entryKey, expansion);
        stateRef.current = next;
        setState(next);
    }, [expansionHistory]);

    return (
        <ul className="location-list" role="list" aria-label={t('Lista miast')}>
            {groups.map((city, cityIndex) => (
                <LocationCity key={city.id} city={city} contentId={`location-city-${cityIndex}`}
                    open={expandedCity?.id === city.id}
                    expandedObjectId={expandedCity?.id === city.id ? expandedObject?.id : undefined}
                    sort={sort} position={position} onToggle={toggleGroup} />
            ))}
        </ul>
    );
};
